/**
 * Search Metadata Store
 *
 * Central store for managing search metadata and recommendations from chat streams.
 * Provides real-time updates to results pages as recommendations arrive.
 */

import type { ShopperSearchRequest } from "openapi/shop_agent/client/types.gen";

export interface SearchIntent {
  q: string;
  facets: Array<{ name: string; values: string[] }>;
}

export interface SearchMetadata {
  searchId: string;
  query: string;
  facets: Array<{ name: string; values: string[] }>;
  priceMin?: number | null;
  priceMax?: number | null;
  similarToBrands?: string[]; // Brands for style-based similarity search
  rankingText?: string | null; // Text for re-ranking search results
  rankingEmbeddingColumns?: string[]; // Embedding columns for ranking
  retrievalEmbeddingColumns?: string[]; // Embedding columns for retrieval
  browseMenuUuid?: string | null; // Browse menu uuid for breadcrumb/sidebar mapping
  /** The search as a `/v1` request: the shopper's words and the agent's caller facets. */
  intent?: SearchIntent | null;
  /**
   * A stored `/v1` request recovered from history; replayed exactly as stored.
   * Kept only by a write that passes it, so a newer search never replays it.
   */
  request?: ShopperSearchRequest | null;
  recommendations?: {
    brands: Array<{
      brand: string;
      justification?: string;
    }>;
    attributes: Array<{
      attribute: string;
      value: string;
      justification?: string;
    }>;
    justification?: string; // Overall justification for this search's recommendations
  };
  isComplete: boolean;
  timestamp: number;
}

type Listener = (metadata: SearchMetadata) => void;

class SearchMetadataStore {
  private metadata: Map<string, SearchMetadata> = new Map();
  private listeners: Map<string, Set<Listener>> = new Map();
  private ttl: number = 60 * 60 * 1000; // 1 hour in milliseconds

  constructor() {
    // Only run browser-specific code in browser environment
    if (typeof window !== "undefined") {
      // Load from sessionStorage on initialization
      this.loadFromSessionStorage();

      // Clean up expired entries periodically
      setInterval(() => this.cleanup(), 5 * 60 * 1000); // Every 5 minutes
    }
  }

  /**
   * Store search metadata from tool event
   */
  setSearchMetadata(
    searchId: string,
    query: string,
    facets: Array<{ name: string; values: string[] }>,
    options?: {
      priceMin?: number | null;
      priceMax?: number | null;
      similarToBrands?: string[];
      rankingText?: string | null;
      rankingEmbeddingColumns?: string[];
      retrievalEmbeddingColumns?: string[];
      browseMenuUuid?: string | null;
      intent?: SearchIntent | null;
      request?: ShopperSearchRequest | null;
      /** Override the searchId stored in metadata (useful when storing by threadId but preserving original searchId) */
      originalSearchId?: string;
    }
  ): void {
    const existing = this.metadata.get(searchId);
    const hasPriceMin =
      options !== undefined && Object.prototype.hasOwnProperty.call(options, "priceMin");
    const hasPriceMax =
      options !== undefined && Object.prototype.hasOwnProperty.call(options, "priceMax");
    const hasSimilarToBrands =
      options !== undefined && Object.prototype.hasOwnProperty.call(options, "similarToBrands");
    const hasRankingText =
      options !== undefined && Object.prototype.hasOwnProperty.call(options, "rankingText");
    const hasRankingEmbeddingColumns =
      options !== undefined &&
      Object.prototype.hasOwnProperty.call(options, "rankingEmbeddingColumns");
    const hasRetrievalEmbeddingColumns =
      options !== undefined &&
      Object.prototype.hasOwnProperty.call(options, "retrievalEmbeddingColumns");
    const hasBrowseMenuUuid =
      options !== undefined && Object.prototype.hasOwnProperty.call(options, "browseMenuUuid");
    const hasIntent =
      options !== undefined && Object.prototype.hasOwnProperty.call(options, "intent");
    const hasRequest =
      options !== undefined && Object.prototype.hasOwnProperty.call(options, "request");

    // Use originalSearchId if provided (for threadId-keyed entries), otherwise use the key
    const effectiveSearchId = options?.originalSearchId || searchId;

    const metadata: SearchMetadata = {
      searchId: effectiveSearchId,
      query,
      facets,
      priceMin: hasPriceMin ? (options?.priceMin ?? null) : existing?.priceMin,
      priceMax: hasPriceMax ? (options?.priceMax ?? null) : existing?.priceMax,
      similarToBrands: hasSimilarToBrands ? options?.similarToBrands : existing?.similarToBrands,
      rankingText: hasRankingText ? (options?.rankingText ?? null) : existing?.rankingText,
      rankingEmbeddingColumns: hasRankingEmbeddingColumns
        ? options?.rankingEmbeddingColumns
        : existing?.rankingEmbeddingColumns,
      retrievalEmbeddingColumns: hasRetrievalEmbeddingColumns
        ? options?.retrievalEmbeddingColumns
        : existing?.retrievalEmbeddingColumns,
      browseMenuUuid: hasBrowseMenuUuid
        ? (options?.browseMenuUuid ?? null)
        : existing?.browseMenuUuid,
      intent: hasIntent ? (options?.intent ?? null) : existing?.intent,
      request: hasRequest ? (options?.request ?? null) : null,
      recommendations: existing?.recommendations,
      isComplete: existing?.isComplete || false,
      timestamp: Date.now(),
    };

    this.metadata.set(searchId, metadata);
    this.saveToSessionStorage();
    this.notifyListeners(searchId, metadata);
  }

  /**
   * Update recommendations when final message arrives
   */
  updateRecommendations(
    searchId: string,
    recommendations: {
      brands: Array<{ brand: string; justification?: string }>;
      attributes: Array<{
        attribute: string;
        value: string;
        justification?: string;
      }>;
      justification?: string;
    }
  ): void {
    const existing = this.metadata.get(searchId);

    if (!existing) {
      console.warn(`No metadata found for search ${searchId}`);
      return;
    }

    const updated: SearchMetadata = {
      ...existing,
      recommendations,
      timestamp: Date.now(),
    };

    this.metadata.set(searchId, updated);
    this.saveToSessionStorage();
    this.notifyListeners(searchId, updated);
  }

  /**
   * Mark search as complete (all data received)
   */
  markComplete(searchId: string): void {
    const existing = this.metadata.get(searchId);

    if (!existing) {
      console.warn(`No metadata found for search ${searchId}`);
      return;
    }

    const updated: SearchMetadata = {
      ...existing,
      isComplete: true,
      timestamp: Date.now(),
    };

    this.metadata.set(searchId, updated);
    this.saveToSessionStorage();
    this.notifyListeners(searchId, updated);
  }

  /**
   * Get metadata for a search
   */
  getSearchMetadata(searchId: string): SearchMetadata | null {
    return this.metadata.get(searchId) || null;
  }

  /**
   * Subscribe to updates for a specific search
   * Returns unsubscribe function
   */
  subscribeToSearch(searchId: string, callback: Listener): () => void {
    if (!this.listeners.has(searchId)) {
      this.listeners.set(searchId, new Set());
    }

    const listeners = this.listeners.get(searchId);
    if (listeners) {
      listeners.add(callback);
    }

    // Return unsubscribe function
    return () => {
      const listeners = this.listeners.get(searchId);
      if (listeners) {
        listeners.delete(callback);
        if (listeners.size === 0) {
          this.listeners.delete(searchId);
        }
      }
    };
  }

  /**
   * Notify all listeners for a search
   */
  private notifyListeners(searchId: string, metadata: SearchMetadata): void {
    const listeners = this.listeners.get(searchId);
    if (listeners) {
      listeners.forEach((listener) => listener(metadata));
    }
  }

  /**
   * Clean up expired entries
   */
  private cleanup(): void {
    const now = Date.now();
    const expired: string[] = [];

    this.metadata.forEach((metadata, searchId) => {
      if (now - metadata.timestamp > this.ttl) {
        expired.push(searchId);
      }
    });

    expired.forEach((searchId) => {
      this.metadata.delete(searchId);
      this.listeners.delete(searchId);
    });

    if (expired.length > 0) {
      this.saveToSessionStorage();
    }
  }

  /**
   * Save to sessionStorage for persistence across navigation
   */
  private saveToSessionStorage(): void {
    if (typeof window === "undefined") return;

    try {
      const data = Array.from(this.metadata.entries());
      sessionStorage.setItem("searchMetadata", JSON.stringify(data));
    } catch (error) {
      console.error("Failed to save to sessionStorage:", error);
    }
  }

  /**
   * Load from sessionStorage on initialization
   */
  private loadFromSessionStorage(): void {
    if (typeof window === "undefined") return;

    try {
      const data = sessionStorage.getItem("searchMetadata");
      if (data) {
        const entries: [string, SearchMetadata][] = JSON.parse(data);
        this.metadata = new Map(entries);
      }
    } catch (error) {
      console.error("Failed to load from sessionStorage:", error);
    }
  }

  /**
   * Clear all metadata (useful for testing)
   */
  clear(): void {
    this.metadata.clear();
    this.listeners.clear();
    if (typeof window !== "undefined") {
      sessionStorage.removeItem("searchMetadata");
    }
  }
}

// Singleton instance
export const searchMetadataStore = new SearchMetadataStore();
